import {
  FaDesktop,
  FaLaptop,
  FaMobileAlt,
  FaFingerprint,
  FaRegClock,
  FaChrome,
  FaEdge,
  FaFirefoxBrowser,
} from "react-icons/fa";

type Device = {
  id: string;
  name: string;
  browser: "Chrome" | "Edge" | "Firefox";
  os: string;
  terminal: string;
  lastActive: string;
  fingerprint: boolean;
  current: boolean;
  disabled: boolean;
};

const devices: Device[] = [
  {
    id: "1",
    name: "Reception Desktop",
    browser: "Chrome",
    os: "Windows 11",
    terminal: "Reception Terminal",
    lastActive: "Just now",
    fingerprint: true,
    current: true,
    disabled: false,
  },
  {
    id: "2",
    name: "Manager Laptop",
    browser: "Edge",
    os: "Windows 11",
    terminal: "Manager Terminal",
    lastActive: "2 minutes ago",
    fingerprint: false,
    current: false,
    disabled: false,
  },
  {
    id: "3",
    name: "Restaurant Tablet",
    browser: "Chrome",
    os: "Android 15",
    terminal: "Restaurant Terminal",
    lastActive: "Yesterday",
    fingerprint: true,
    current: false,
    disabled: true,
  },
  {
    id: "4",
    name: "Accounting PC",
    browser: "Firefox",
    os: "Ubuntu 24.04",
    terminal: "Accounting Terminal",
    lastActive: "3 days ago",
    fingerprint: false,
    current: false,
    disabled: false,
  },
];

const browserIcon = (browser: Device["browser"]) => {
  switch (browser) {
    case "Chrome":
      return FaChrome({});
    case "Edge":
      return FaEdge({});
    case "Firefox":
      return FaFirefoxBrowser({});
  }
};

const deviceIcon = (device: Device) => {
  if (device.os.toLowerCase().includes("android"))
    return FaMobileAlt({ size: 26 });

  if (device.name.toLowerCase().includes("laptop"))
    return FaLaptop({ size: 26 });

  return FaDesktop({ size: 26 });
};

export default function ConnectedDevices() {
  return (
    <div className="space-y-5">

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            Connected Devices
          </h2>

          <p className="text-gray-500 text-sm mt-1">
            Manage devices currently signed into your account.
          </p>
        </div>

        <button className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700">
          Logout Others
        </button>
      </div>

      {devices.map((device) => (
        <div
          key={device.id}
          className="bg-white rounded-2xl border shadow-sm p-6"
        >
          {/* Header */}
          <div className="flex justify-between gap-4">

            <div className="flex gap-4">

              <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                {deviceIcon(device)}
              </div>

              <div>

                <div className="flex flex-wrap items-center gap-2">

                  <h3 className="font-semibold text-lg">
                    {device.name}
                  </h3>

                  {device.current ? (
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      ● Current Device
                    </span>
                  ) : (
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        device.disabled
                          ? "bg-red-100 text-red-600"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {device.disabled ? "Disabled" : "Active"}
                    </span>
                  )}

                </div>

                <div className="mt-2 flex flex-wrap gap-5 text-gray-600 text-sm">

                  <span className="flex items-center gap-2">
                    {browserIcon(device.browser)}
                    {device.os}
                  </span>

                  <span className="flex items-center gap-2">
                    {browserIcon(device.browser)}
                    {device.browser}
                  </span>

                </div>

                <p className="text-sm text-gray-500 mt-2">
                  {device.terminal}
                </p>

              </div>

            </div>

          </div>

          {/* Divider */}
          <div className="border-t my-5" />

          {/* Footer */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

            <div className="space-y-2 text-sm">

              <div className="flex items-center gap-2 text-gray-600">
                {FaRegClock({})}
                <span>
                  Last Active: <strong>{device.lastActive}</strong>
                </span>
              </div>

              <div
                className={`flex items-center gap-2 ${
                  device.fingerprint
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {FaFingerprint({})}

                <span>
                  Fingerprint{" "}
                  <strong>
                    {device.fingerprint
                      ? "Enabled"
                      : "Disabled"}
                  </strong>
                </span>
              </div>

            </div>

            {!device.current && (
              <div className="flex gap-3">

                <button
                  className={`px-4 py-2 rounded-lg font-medium ${
                    device.disabled
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-yellow-500 text-white hover:bg-yellow-600"
                  }`}
                >
                  {device.disabled
                    ? "Enable"
                    : "Disable"}
                </button>

                <button className="px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
                  Remove
                </button>

              </div>
            )}

          </div>
        </div>
      ))}
    </div>
  );
}